import { FC, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '@/services';
import type { DashboardData } from '@/types/dashboard';
import ProfileTile from '@/components/dashboard/ProfileTile';
import RankingTile from '@/components/dashboard/RankingTile';
import LatestCoursesCube from '@/components/dashboard/LatestCoursesCube';
import MissionsCube from '@/components/dashboard/MissionsCube';
import QuickLinksGrid from '@/components/dashboard/QuickLinksGrid';
import CoursesInProgressTile from '@/components/dashboard/CoursesInProgressTile';
import TileSkeleton from '@/components/dashboard/TileSkeleton';
import PinnedAgentsSection from '@/components/dashboard/PinnedAgentsSection';

const DashboardPage: FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const result = await dashboardService.getDashboardData();
      setData(result);
    } catch (err) {
      console.error('Erro ao carregar dashboard:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch + refresh every 60s + on visibility change
  useEffect(() => {
    fetchData();

    const interval = setInterval(fetchData, 60000);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchData();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchData]);

  return (
    <div className="flex flex-1 justify-center py-8 bg-surface-primary">
      <main className="flex flex-1 justify-center">
        <div className="layout-content-container flex flex-col max-w-[1200px] flex-1 px-4 md:px-10 gap-8">
          {/* Hero Banner */}
          <section>
            <div
              className="tema-escuro-fixo flex min-h-[200px] flex-col gap-4 bg-cover bg-center bg-no-repeat rounded-xl items-start justify-center px-6 py-8 md:px-12 relative overflow-hidden"
              style={{
                backgroundImage: 'linear-gradient(135deg, #003046 0%, #005577 60%, #003046 100%)'
              }}
            >
              <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-accent/10 to-transparent pointer-events-none" />
              <div className="flex flex-col gap-3 text-left z-10 max-w-2xl">
                <h1 className="text-white text-3xl md:text-5xl font-black leading-tight tracking-tight">
                  Aprender, aplicar,<br />
                  {/* Faixa navy fixa: o amarelo da marca nos dois temas (o accent do claro é navy). */}
                  <span className="text-[#FCB428]">transformar.</span>
                </h1>
                <p className="text-slate-300 text-sm md:text-base font-medium max-w-md">
                  Sua jornada de Inteligência Artificial começa aqui.
                </p>
              </div>
              <Link
                to="/trilhas"
                className="z-10 flex min-w-[140px] cursor-pointer items-center justify-center rounded-lg h-10 px-5 bg-[#FCB428] text-[#001420] text-sm font-bold transition-all hover:bg-[#FFC94D] hover:scale-105 active:scale-95 shadow-lg shadow-accent/20"
              >
                Começar agora
                <span className="material-symbols-outlined ml-2 text-sm">trending_flat</span>
              </Link>
            </div>
          </section>

          {/* Tile Grid */}
          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <TileSkeleton wide />
              <TileSkeleton />
              <TileSkeleton />
              <TileSkeleton />
              <TileSkeleton />
              <TileSkeleton wide />
            </div>
          ) : data ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tile-grid">
              {/* Row 1 */}
              <ProfileTile profile={data.userProfile} />
              <RankingTile ranking={data.ranking} />
              <MissionsCube
                mission={data.currentMission}
                achievement={data.lastAchievement}
                streak={data.streak}
              />

              {/* Row 2 */}
              <LatestCoursesCube courses={data.latestCourses} />
              <QuickLinksGrid hubs={data.hubs} />
              <CoursesInProgressTile courses={data.recentCourses.inProgress} />
            </div>
          ) : (
            <div className="flex items-center justify-center p-12">
              <p className="text-content-muted">Erro ao carregar dados do dashboard</p>
            </div>
          )}

          {/* Meus agentes (J05): atalhos pros agentes fixados; some quando não há nenhum */}
          <PinnedAgentsSection />
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
